<template>
  <div class="kl-module" :class="{ open: expanded }">
    <div class="kl-seg-head" @click="expanded = !expanded">
      <span class="kl-caret">&#9656;</span>
      <div class="kl-seg-titles">
        <div class="kl-seg-top">
          <span class="kl-seg-order">{{ module.order }}</span>
          <span class="kl-seg-title">
            {{
              module.title ||
              $t("teaching.instructional.moduleUntitled", {
                entity: containerEntityName,
              })
            }}
            <!-- La Unidad se compone ACÁ, al mostrar. Antes venía escrita dentro del
                 título guardado, así que al editar el RA el Módulo seguía anunciando el
                 nombre viejo —y para siempre, porque nadie lo recalculaba—. -->
            <span v-if="moduleUnitLabel" class="kl-seg-unit">
              · {{ moduleUnitLabel }}
            </span>
          </span>
          <span v-if="allows_crud" class="ml-auto" @click.stop>
            <ButtonSmall
              :tooltip_text="
                $t('reusable.common.createTooltip', {
                  entity: activityEntityName,
                })
              "
              :click_button="
                () =>
                  $bvModal.show(
                    `instructional-module-${module.id}-new-activity-modal`
                  )
              "
              :icon="'plus'"
            ></ButtonSmall>
            <ButtonSmall
              :tooltip_text="
                $t('reusable.common.editTooltip', {
                  entity: containerEntityName,
                })
              "
              :click_button="
                () =>
                  $bvModal.show(
                    `instructional-module-edit-modal-${module.id}`
                  )
              "
              :icon="'square'"
            ></ButtonSmall>
            <ButtonSmall
              :tooltip_text="$t('reusable.common.delete')"
              :click_button="askDelete"
              :icon="'trash'"
            ></ButtonSmall>
          </span>
        </div>

        <!-- Los RA NO se eligen: son los que traen las actividades de adentro. -->
        <div class="kl-seg-units">
          <span class="kl-units-label">
            {{
              $getVisibleNames(
                "teaching.studyunit",
                true,
                "Unidades de Estudio"
              )
            }}:
          </span>
          <span
            v-for="study_unit in moduleStudyUnits"
            :key="study_unit.id"
            class="kl-chip"
            :title="study_unit.name"
          >
            {{ $t("reusable.common.numberAbbreviation")
            }}{{ getCompetenceUnitOrder(study_unit.temp_competence_unit)
            }}{{ study_unit.order }}
          </span>
          <span v-if="moduleStudyUnits.length === 0" class="kl-units-empty">
            {{
              $t("teaching.instructional.moduleStudyUnitsEmpty", {
                entity: piecesEntityNamePlural,
              })
            }}
          </span>
        </div>

        <!-- E47 · La secuencia del modelo, incompleta en este Módulo.
             Va en la cabecera y no adentro porque es una lectura del Módulo entero, y
             tiene que verse con el Módulo cerrado: si hay que abrirlo para enterarse, el
             aviso llega cuando el usuario ya fue a buscar otra cosa.
             AVISA y no impide, igual que el salto de secuencia del Estado del Diseño: el
             docente puede tener una razón, y hay diseños ya cargados que no lo cumplen. -->
        <div v-if="missingMoments.length > 0" class="kl-seg-moments-missing">
          <b-icon icon="exclamation-triangle-fill" scale="0.8"></b-icon>
          {{
            $t("teaching.instructional.moduleMomentsIncomplete", {
              container: containerEntityName,
              moments: missingMoments.map((x) => x.name).join(", "),
            })
          }}
        </div>

        <!-- E47 · El cierre del curso que no cierra. Causa distinta a la de arriba y por
             eso texto propio: al Módulo integrador no le "faltan momentos" —no recorre un
             ciclo—, pero sí tiene que llegar al último del método, que es lo que lo hace
             un cierre. Son excluyentes: el aviso de arriba no mira los transversales. -->
        <div v-if="unreachedClosingMoment" class="kl-seg-moments-missing">
          <b-icon icon="exclamation-triangle-fill" scale="0.8"></b-icon>
          {{
            $t("teaching.instructional.moduleClosingMomentMissing", {
              container: containerEntityName,
              moment: unreachedClosingMoment.name,
            })
          }}
        </div>
      </div>
    </div>

    <!-- Montados y ocultos, no condicionados: al imprimir el `@media print` los muestra
         sin esperar un re-render que el navegador no espera. -->
    <div v-show="expanded && module.description" class="kl-seg-desc">
      <RenderVHtml :content="module.description"></RenderVHtml>
    </div>

    <div v-show="expanded" class="kl-seg-body">
      <!-- E30 · Las actividades cuelgan del Modulo, no de un momento. Los momentos
           dejaron de ser cajas: cada tarjeta muestra los que cumple en su fila de
           píldoras, que es lo que permite que cumpla varios sin estar metida en uno. -->
      <ActivityList
        :activities="moduleActivities"
        :instructional_design_id="instructional_design_id"
        :module_id="module.id"
        :study_units="study_units"
        :bibliographic_resources="bibliographic_resources"
        :teaching_support_resources="teaching_support_resources"
        :activity_weeks="activity_weeks"
        :time_allocation_rows="time_allocation_rows"
        :evaluation_situations="evaluation_situations"
        :evaluation_list="evaluation_list"
        :highlight_activity_id="highlight_activity_id"
        :level_key="level_key"
        :allows_crud="allows_crud"
        :shows_ai="shows_ai"
        :egress_profile_matter_id="egress_profile_matter_id"
        @reordered="$emit('reordered')"
        @resources-changed="$emit('resources-changed')"
        :empty_text="
          $t('teaching.instructional.moduleNoActivities', {
            entity: containerEntityName,
            activities: piecesEntityNamePlural,
          })
        "
      ></ActivityList>
    </div>

    <b-modal
      :key="`module-edit-${module.id}`"
      :id="`instructional-module-edit-modal-${module.id}`"
      :title="
        $t('reusable.common.editModalTitle', { entity: containerEntityName })
      "
      size="lg"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @show="form_key++"
    >
      <ModuleForm
        :key="form_key"
        :module="module"
        :instructional_design_id="instructional_design_id"
        @saved="closeEditModal"
      ></ModuleForm>
    </b-modal>

    <b-modal
      :key="`module-new-activity-${module.id}`"
      :id="`instructional-module-${module.id}-new-activity-modal`"
      :title="
        $t('reusable.common.createModalTitle', { entity: activityEntityName })
      "
      size="xl"
      hide-footer
      ignore-enforce-focus-selector="#sidebar-right"
      @show="activity_form_key++"
    >
      <!-- E38 · El mismo formulario del Programa que usa la edición. El guardado no se hace
           acá: se emite al orquestador, que es el que conoce el nivel y la secuencia completa
           del diseño. -->
      <MatterProgramEvaluationForm
        v-if="level_key === 'matter'"
        :key="activity_form_key"
        :Evaluation="null"
        :matter_id="matter_base_id"
        :EvaluationSituations="evaluation_situations"
        :MatEvaStuEnvTimAll="time_allocation_rows"
        :evaluation_list="evaluation_list"
        @created="
          (evaluation, time_rows, tests) =>
            $emit('created', evaluation, time_rows, tests, module.id)
        "
        @created_support_resource="$emit('resources-changed')"
      ></MatterProgramEvaluationForm>
      <EPMatterProgramEvaluationForm
        v-else
        :key="activity_form_key"
        :Evaluation="null"
        :egress_profile_matter_id="egress_profile_matter_id"
        :EvaluationSituations="evaluation_situations"
        :EPMatEvaStuEnvTimAll="time_allocation_rows"
        :evaluation_list="evaluation_list"
        @created="
          (evaluation, time_rows, tests) =>
            $emit('created', evaluation, time_rows, tests, module.id)
        "
        @created_support_resource="$emit('resources-changed')"
      ></EPMatterProgramEvaluationForm>
    </b-modal>
  </div>
</template>

<script>
import * as names from "@/store/names";
import { mapGetters } from "vuex";
import { toast } from "@/utils/utils";

export default {
  name: "ModuleContainer",
  components: {
    RenderVHtml: () => import("@/components/reusable/RenderVHtml"),
    ActivityList: () => import("./ActivityList"),
    ModuleForm: () => import("./ModuleForm"),
    MatterProgramEvaluationForm: () =>
      import(
        "@/components/teaching/MatterProgram/MatterProgramEvaluation/MatterProgramEvaluationForm"
      ),
    EPMatterProgramEvaluationForm: () =>
      import(
        "@/components/teaching/MatterProgram/EPMatterProgramEvaluation/EPMatterProgramEvaluationForm"
      ),
    ButtonSmall: () =>
      import("@/components/reusable/Buttons/Small/ButtonSmall"),
  },
  props: {
    module: {
      type: Object,
      required: true,
    },
    instructional_design_id: {
      type: Number,
      required: true,
    },
    // Actividades del diseño; se filtran por modulo.
    activities: {
      type: Array,
      default: () => [],
    },
    // Momentos del método activo, en orden.
    moments: {
      type: Array,
      default: () => [],
    },
    // StudyUnits de la Asignatura (Programa).
    study_units: {
      type: Array,
      default: () => [],
    },
    // Catálogo de Recursos Bibliográficos de la Asignatura; viaja hasta
    // ActivityCard para pintar y editar la bibliografía de cada actividad.
    bibliographic_resources: {
      type: Array,
      default: () => [],
    },
    // Catálogo de Recursos de Apoyo a la Docencia de la Asignatura, con el
    // nombre de su tipo ya resuelto. En el Programa este campo sólo se muestra
    // para el tipo "Actividad", así que es de la actividad en todo salvo dónde
    // se guarda: el dueño sigue siendo el wrapper (E16).
    teaching_support_resources: {
      type: Array,
      default: () => [],
    },
    // Semana calculada de cada actividad: Map(activity_id -> {from,to,overflow}).
    // Se deriva en el orquestador porque necesita el diseño completo.
    activity_weeks: {
      type: Map,
      default: () => new Map(),
    },
    // Actividad a la que se llega por deep link desde el Programa. El modulo
    // que la contiene se abre solo para que la tarjeta pueda montarse.
    // E38 · Lo que el formulario del Programa pide como props: las horas por Ambiente de
    // Aprendizaje y el catálogo de situaciones evaluativas. Viajan desde el orquestador.
    time_allocation_rows: {
      type: Array,
      default: () => [],
    },
    evaluation_situations: {
      type: Array,
      default: () => [],
    },
    // Todas las piezas de la asignatura: el formulario las usa para el tope del 100%.
    evaluation_list: {
      type: Array,
      default: () => [],
    },
    highlight_activity_id: {
      type: Number,
      default: null,
    },
    // E37 · Nivel del wrapper; baja hasta ActivityList, que decide a qué endpoint PATCHea.
    level_key: {
      type: String,
      default: "egress_profile_matter",
    },
    // E38 · La asignatura a la que se cuelga lo que se cree desde este Modulo. Viajan las
    // dos porque el formulario que se monta depende del nivel, y el Modulo puede estar
    // vacío: no hay actividad de la cual deducirlas.
    matter_base_id: {
      type: Number,
      default: null,
    },
    egress_profile_matter_id: {
      type: Number,
      default: null,
    },
    allows_crud: {
      type: Boolean,
      default: false,
    },
    // E45 · De paso hacia ActivityCard, que es donde vive el botón de regenerar con IA.
    // La Asignatura del Perfil de Egreso que el builder necesita ya la recibía.
    shows_ai: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Cerrado por defecto: la vista arranca como un índice de modulos.
      expanded: false,
      form_key: 0,
      activity_form_key: 0,
    };
  },
  computed: {
    // Las dos cosas que puede alojar un Modulo: desde E37 no son sólo Actividades.
    piecesEntityNamePlural() {
      return `${this.$getVisibleNames(
        "manual.instructional_design.activity",
        true,
        "Actividades"
      )}/${this.$getVisibleNames(
        "evaluations2.evaluation",
        true,
        "Evaluaciones"
      )}`;
    },
    ...mapGetters({
      competence_units: names.COMPETENCE_UNITS,
      evaluation_criterias: names.EVALUATION_CRITERIAS,
      instructional_designs: names.INSTRUCTIONAL_DESIGNS,
    }),
    containerEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.container",
        false,
        "Módulo"
      );
    },
    activityEntityName() {
      return this.$getVisibleNames(
        "manual.instructional_design.activity",
        false,
        "Actividad"
      );
    },
    moduleActivities() {
      return this.activities.filter((x) => x.module == this.module.id);
    },
    // La Unidad que el Módulo anuncia al lado de su título. Sólo cuando es UNA: el
    // Módulo de cierre las lleva todas y enumerarlas ahí no dice nada. El nombre sale de
    // `study_units`, que la vista ya normalizó —con su número y su RA cuando no tiene
    // nombre—, así que cambia solo si alguien edita la Unidad.
    moduleUnitLabel() {
      const unidades = this.moduleStudyUnits;
      return unidades.length === 1 ? unidades[0].name : null;
    },
    /**
     * E47 · Los momentos del método que este Módulo no trabaja.
     *
     * El método instruccional es un recorrido que se cierra DENTRO del Módulo —es lo que
     * hace el builder al repartir el momento a lo largo de cada ciclo—, así que el Módulo
     * es el único lugar donde "falta un momento" quiere decir algo. El Estado del Diseño
     * mira todo el diseño junto y por eso no puede verlo: un diseño donde el primer
     * Módulo recorre el método completo y los otros tres sólo activan marca todos los
     * momentos como trabajados.
     *
     * Mismo criterio de pertenencia que la distribución del Estado del Diseño (E46):
     * cuenta la pieza que CUMPLE el momento (la M2M de E30) y también la que sólo lo trae
     * en `moment`, por si alguna ficha se guardó sin la M2M sembrada.
     *
     * Dos casos en los que no hay nada que avisar:
     * - El Módulo vacío. Todavía no empezó; no es una secuencia interrumpida, y avisarle
     *   a cada Módulo recién creado que le faltan todos los momentos es ruido que se
     *   aprende a ignorar —y con él se ignora el aviso que sí importa—.
     * - El Módulo transversal, que por definición lleva TODAS las Unidades y no recorre
     *   ningún ciclo (ver `InstructionalModule` en el backend).
     */
    missingMoments() {
      if (this.moments.length === 0) return [];
      if (this.moduleActivities.length === 0) return [];
      if (this.isCrossCuttingModule) return [];
      return this.moments.filter(
        (moment) =>
          !this.moduleActivities.some(
            (x) =>
              x.moment == moment.id || (x.moments || []).includes(moment.id)
          )
      );
    },
    /**
     * E47 · El Cierre del curso que no llega al último momento del método.
     *
     * El Módulo integrador está fuera del aviso de arriba porque no recorre un ciclo: no
     * tiene por qué pasar por todos los momentos. Pero sí tiene que alcanzar el último
     * —es lo que lo vuelve un cierre, y es lo que el builder le asigna por defecto
     * (`momentsByRow`: la fila sin Unidad recibe `method_moments.slice(-1)`)—, y hoy un
     * cierre que declara sólo un momento temprano no lo nota nadie: el medidor de
     * secuencia del Estado del Diseño sólo marca a la pieza que salta HACIA ADELANTE.
     *
     * Se pregunta si alguna pieza cumple el último momento y no por el mayor orden
     * alcanzado: así una integradora que declara Activación e Integración a la vez pasa
     * limpia —es el caso normal de una pieza que cumple varios momentos (E30)— y sólo
     * queda marcada la que de verdad se quedó corta.
     */
    unreachedClosingMoment() {
      if (this.moments.length === 0) return null;
      if (!this.isCrossCuttingModule) return null;
      if (this.moduleActivities.length === 0) return null;
      // Por `order` y no por la posición en el array: el orden del recorrido lo manda el
      // método, y esta vista recibe los momentos ya ordenados pero no los ordena.
      const last = this.moments.reduce((a, b) =>
        (b.order || 0) > (a.order || 0) ? b : a
      );
      const reaches = this.moduleActivities.some(
        (x) => x.moment == last.id || (x.moments || []).includes(last.id)
      );
      return reaches ? null : last;
    },
    // El Módulo que aloja la transversal es el que lleva todas las Unidades. El `> 1` es
    // para la Asignatura de una sola Unidad, donde esa cuenta daría transversales a
    // todos los Módulos.
    isCrossCuttingModule() {
      return (
        this.study_units.length > 1 &&
        this.moduleStudyUnits.length === this.study_units.length
      );
    },
    // Se derivan de las actividades, no de una selección del usuario.
    // E37 · Los RA del Modulo se derivan de los IL de sus piezas. Antes se leían de
    // `activity.study_units`, un campo de la ficha del diseño; el wrapper no lo tiene —los RA
    // siempre se dedujeron de los IL (E12)— así que sin esto la lista salía vacía siempre y el
    // Modulo decía "Se mostrarán al crear actividades" aunque tuviera actividades adentro.
    moduleStudyUnits() {
      // Lo declarado manda, pero ya no se declara a mano: el formulario del Modulo dejó
      // de pedirlo porque la tarjeta las deduce sola. Sólo las trae el Modulo creado por
      // el generador con IA, que sí deja sus `InstructionalUnit` escritas.
      const declaradas = this.module.study_units || [];
      if (declaradas.length > 0) {
        return this.study_units
          .filter((x) => declaradas.includes(x.id))
          .sort((a, b) => a.order - b.order);
      }
      const indicator_by_id = new Map(
        this.evaluation_criterias.map((x) => [x.id, x])
      );
      const study_unit_ids = new Set();
      this.moduleActivities.forEach((activity) => {
        (activity.evaluation_criterias_micro || []).forEach((id) => {
          const indicator = indicator_by_id.get(id);
          if (indicator) study_unit_ids.add(indicator.study_unit);
        });
      });
      return this.study_units
        .filter((x) => study_unit_ids.has(x.id))
        .sort((a, b) => a.order - b.order);
    },
  },
  watch: {
    // Las actividades llegan después del montaje, así que no basta con mirarlas
    // en `created`. Sólo abre; no cierra si el usuario lo colapsó a mano.
    moduleActivities: {
      immediate: true,
      handler(activities) {
        if (this.highlight_activity_id == null || this.expanded) return;
        if (activities.some((x) => x.id == this.highlight_activity_id))
          this.expanded = true;
      },
    },
  },
  methods: {
    // Prefijo de la Unidad de Competencia, cuando la institución las usa.
    getCompetenceUnitOrder(temp_competence_unit_id) {
      const competence_unit = this.competence_units.find(
        (x) => x.id == temp_competence_unit_id
      );
      return competence_unit ? competence_unit.order + "." : "";
    },
    // Relee las piezas del nivel. Es lo mismo que hace el orquestador al abrir la vista; acá se
    // repite porque el borrado del Modulo las deja sin `module` en la base y el store no
    // se entera solo.
    refetchEvaluations() {
      const design = this.instructional_designs.find(
        (x) => x.id == this.instructional_design_id
      );
      if (!design) return;
      if (this.level_key === "matter") {
        if (design.matter)
          this.$store.dispatch(names.FETCH_MATTER_EVALUATIONS, design.matter);
        return;
      }
      if (design.egress_profile_matter)
        this.$store.dispatch(
          names.FETCH_EGRESS_PROFILE_MATTER_EVALUATIONS,
          design.egress_profile_matter
        );
    },
    closeEditModal() {
      this.$bvModal.hide(`instructional-module-edit-modal-${this.module.id}`);
    },
    // E37 · El aviso cambió porque el comportamiento cambió. `module` pasó a ser `SET_NULL`
    // (antes era CASCADE sobre la ficha del diseño), así que borrar un Modulo **ya no
    // elimina nada**: sus piezas son evaluaciones reales del Programa, con instrumentos y notas,
    // y lo único que pierden es su lugar en la secuencia. Prometer que se eliminan sería mentir
    // sobre lo que hace el botón.
    askDelete() {
      this.$swal({
        title: this.$t("reusable.common.deleteConfirmTitle2", {
          entity: this.containerEntityName,
          article: "la",
        }),
        text: this.moduleActivities.length
          ? this.$t("teaching.instructional.moduleDeleteKeepsActivities", {
              count: this.moduleActivities.length,
              container: this.containerEntityName,
              activities: this.piecesEntityNamePlural,
            })
          : null,
        type: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (!result.value) return;
        this.$store
          .dispatch(names.DELETE_INSTRUCTIONAL_MODULE, this.module.id)
          .then(() => {
            // El backend puso en null el `module` de cada pieza, pero el store sigue con el
            // valor viejo: sin releer, las piezas desaparecen de la vista —no pertenecen a
            // ningún modulo dibujado— y sólo reaparecen en "sin Unidad" tras un F5.
            //
            // Se despacha directo y NO se emite al padre: la acción del store borra el
            // modulo y recién después resuelve, así que para cuando llega este `.then()`
            // Vue ya destruyó este componente — y `$destroy()` hace `$off()`, con lo que el
            // `$emit` no le llega a nadie. Un dispatch, en cambio, va al store global y no
            // depende de que el componente siga vivo.
            this.refetchEvaluations();
          })
          .catch(() => {
            toast(this.$t("teaching.instructional.saveError"));
          });
      });
    },
  },
};
</script>

<style scoped>
.kl-module {
  background-color: #fff;
  border: 1.5px solid #bcd3d0;
  border-radius: var(--kl-radius-md, 10px);
  margin-bottom: 1.1rem;
  overflow: hidden;
}
.kl-seg-head {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.75rem 0.9rem;
  background-color: #f2f8f6;
  cursor: pointer;
  user-select: none;
  /* El separador sólo tiene sentido cuando hay cuerpo debajo. */
  border-bottom: 0;
}
.kl-module.open .kl-seg-head {
  border-bottom: 1px solid var(--kl-table-border, #d9e0e0);
}
.kl-seg-head:hover {
  background-color: #eaf3f0;
}
.kl-caret {
  color: #6b7b79;
  margin-top: 0.35rem;
  transition: transform 0.15s;
  flex: none;
}
.kl-module.open .kl-caret {
  transform: rotate(90deg);
}
.kl-seg-titles {
  flex: 1;
  min-width: 0;
  /* `#app` centra todo por defecto; se nota recién cuando el título ocupa el ancho
     completo, que es lo que pasa apenas es largo. */
  text-align: left;
}
.kl-seg-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.kl-seg-order {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: var(--kl-color-accent-medium, #2361a0);
  color: var(--kl-text-on-primary, #fff);
  font-size: var(--kl-fs-caption);
  font-weight: var(--kl-fw-bold, 700);
  flex: none;
  font-variant-numeric: tabular-nums;
}
/* La Unidad acompaña al título pero no compite con él: es contexto, no nombre. */
.kl-seg-unit {
  font-weight: var(--kl-fw-regular, 400);
  color: var(--kl-text-body);
}
.kl-seg-title {
  font-weight: var(--kl-fw-bold, 700);
  color: var(--kl-color-primary, #006ec8);
  font-size: var(--kl-fs-title-sm);
  /* Se queda al lado del número y se parte por dentro.
     `flex-basis: 0` y no `auto`: en un contenedor con `wrap`, la línea se corta mirando
     el ancho de CONTENIDO de cada item —no si puede encogerse—, así que un título largo
     se iba entero al renglón de abajo y dejaba al número solo en el suyo. Con base 0
     siempre entra y después crece. */
  flex: 1 1 0;
}
.kl-seg-units {
  display: flex;
  align-items: center;
  gap: 0.25rem 0.35rem;
  flex-wrap: wrap;
  margin-top: 0.45rem;
}
.kl-units-label {
  font-size: var(--kl-fs-caption);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7b79;
  font-weight: var(--kl-fw-bold, 700);
}
/* E47 · El mismo ámbar que la nota de advertencia del Estado del Diseño (`#8a6800`):
   es el color con el que este diseño ya dice "mirá esto". El ícono acompaña siempre al
   texto, así que el aviso no depende del color para leerse. */
.kl-seg-moments-missing {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  margin-top: 0.3rem;
  font-size: var(--kl-fs-small);
  color: #8a6800;
  text-align: left;
}
.kl-units-empty {
  font-size: var(--kl-fs-caption);
  color: #97a5a3;
  font-style: italic;
}
.kl-chip {
  display: inline-block;
  background-color: var(--kl-chip-bg, #e0f1ff);
  color: var(--kl-chip-text, #006ec8);
  border: 1px solid var(--kl-chip-border, #b6d9f5);
  border-radius: var(--kl-radius-pill, 999px);
  padding: var(--kl-chip-padding, 2px 8px);
  font-size: var(--kl-chip-fs);
  font-weight: var(--kl-chip-fw, 600);
}
.kl-seg-desc {
  padding: 0.6rem 0.9rem 0;
  font-size: var(--kl-fs-body);
  color: #6b7b79;
}
.kl-seg-body {
  padding: 0.75rem 0.9rem;
}
.kl-seg-empty {
  font-size: var(--kl-fs-body);
  color: #97a5a3;
  margin: 0;
  text-align: center;
  padding: 0.5rem 0;
}

/* Impreso, el Módulo se lee entero y sin la caja: la banda de color, el borde
   redondeado y el triángulo son de la pantalla, donde se abre y se cierra. */
@media print {
  .kl-seg-desc,
  .kl-seg-body {
    display: block !important;
  }
  .kl-module {
    border: 0;
    border-radius: 0;
    margin-bottom: 1.4rem;
  }
  .kl-seg-head {
    background-color: transparent;
    cursor: auto;
    padding: 0 0 0.5rem;
    border-bottom: 0;
  }
  .kl-seg-head:hover {
    background-color: transparent;
  }
  .kl-caret {
    display: none;
  }
  .kl-seg-desc,
  .kl-seg-body {
    padding-left: 0;
    padding-right: 0;
  }
  /* El título no se queda solo al pie de una hoja. */
  .kl-seg-head {
    break-after: avoid;
    page-break-after: avoid;
  }
}
</style>
